"use client";

import { sendGTMEvent } from "@next/third-parties/google";
import type { Session } from "@supabase/supabase-js";
import posthog from "posthog-js";
import React, { createContext, useContext, useEffect } from "react";
import { attributionProperties, readAttribution } from "@/lib/attribution";
import { createClient } from "@/utils/supabase/client";

type AuthContextType = {
  userId: string | null;
  session: Session | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SIGNUP_TRACKED_KEY = "sg_signup_tracked";

// Supabase re-emits SIGNED_IN every time it recovers a session (tab refocus,
// page load), and created_at/last_sign_in_at both stay fixed until the user
// signs in again — so the timestamp check alone re-fires signup events for the
// whole first login. Remember which user we already counted.
function claimSignupEvent(userId: string): boolean {
  try {
    if (localStorage.getItem(SIGNUP_TRACKED_KEY) === userId) return false;
    localStorage.setItem(SIGNUP_TRACKED_KEY, userId);
    return true;
  } catch {
    // Storage unavailable (private mode, blocked cookies): keep the event.
    return true;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<Session | null>(null);
  const supabase = createClient();

  useEffect(() => {
    // Initialize session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        setSession(null);
        posthog.reset();
      } else if (event === "SIGNED_IN" && session) {
        setSession(session);

        // Check if this is a new sign-up by comparing timestamps
        const { user } = session;
        if (user?.created_at && user.last_sign_in_at) {
          const createdAt = new Date(user.created_at);
          const lastSignInAt = new Date(user.last_sign_in_at);

          // If the timestamps are within 5 seconds of each other, it's likely a new sign-up
          const timeDifference = Math.abs(
            lastSignInAt.getTime() - createdAt.getTime(),
          );
          const isNewSignUp = timeDifference < 5000; // 5 seconds threshold

          if (isNewSignUp && claimSignupEvent(user.id)) {
            const attribution = attributionProperties(
              readAttribution(document.cookie),
            );
            posthog.identify(user.id);
            posthog.capture("signup_completed", attribution);
            if (Object.keys(attribution).length > 0) {
              posthog.setPersonProperties(attribution);
            }
            sendGTMEvent({
              event: "signup_complete",
            });
          }
        }
      } else if (event === "TOKEN_REFRESHED" && session) {
        // Update session when token is refreshed to ensure access_token is current
        setSession(session);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase.auth]);

  return (
    <AuthContext.Provider
      value={{ userId: session?.user.id ?? null, session: session }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
